import React, { useCallback, useEffect, useState } from "react";

import { MiTareaConNota, misNotasDelAlumno, MisNotas } from "../../../../services/v2/classPortal.service";
import { mensajeDeError, useAlVolver } from "../comun";
import { esNotaBaja, puntosTxt } from "../tareas/comunTareas";
import { BarraCategoria, esBaja, notaTxt, textoAsistencia } from "./comunNotas";
import "../tareas/tareas.css";
import "./notas.css";

/*
	Mis Notas (alumno, Parte C): su promedio, cada categoría con su peso, lo que le falta entregar, lo calificado con la nota y el
	comentario del maestro, y lo entregado que aún no tiene nota. Solo lo suyo (el servidor saca al alumno de la sesión). Si el
	maestro apagó "Sus Notas Al Calificar" ve los comentarios pero ninguna cifra; el promedio del grupo solo llega si el maestro lo
	deja ver y hay al menos 3 alumnos con promedio. Carga al abrir, al volver a la pestaña y con Actualizar; sin sondeos.
*/

const Anillo: React.FC<{ n: number | null; baja: boolean }> = ({ n, baja }) => {
	const r = 52
	const largo = 2 * Math.PI * r
	const lleno = n === null ? 0 : (Math.max(0, Math.min(100, n)) / 100) * largo
	return (
		<div className="ntAnillo">
			<svg role="img" aria-label={n === null ? "Aún sin promedio" : "Promedio " + notaTxt(n) + " de 100"} width="100%" height="100%" viewBox="0 0 120 120">
				<circle cx="60" cy="60" r={r} fill="none" style={{ stroke: "var(--pc-filo-claro)" }} strokeWidth="12" />
				{n !== null && n > 0 ? <circle cx="60" cy="60" r={r} fill="none" style={{ stroke: baja ? "var(--pc-oro)" : "var(--pc-acento)" }} strokeWidth="12" strokeLinecap="round" strokeDasharray={lleno + " " + largo} transform="rotate(-90 60 60)" /> : null}
			</svg>
			<div aria-hidden="true"><span className={"ntCifra" + (baja ? " ntOro" : "")}>{notaTxt(n)}</span><small>{n === null ? "Sin Notas Aún" : "De 100"}</small></div>
		</div>
	)
}

const MisNotasPanel: React.FC<{ h: number; onAbrirTarea: (postId: number) => void }> = ({ h, onAbrirTarea }) => {
	const [d, setD] = useState<MisNotas | null>(null)
	const [error, setError] = useState<string | null>(null)

	const cargar = useCallback(() => {
		misNotasDelAlumno(h).then((r) => { setD(r); setError(null) }).catch((e) => setError(mensajeDeError(e, "No pudimos cargar tus notas.")))
	}, [h])
	useEffect(() => { cargar() }, [cargar])
	useAlVolver(cargar)

	if (error && !d) return <div className="pcAviso pcOro" role="alert">{error}</div>
	if (d === null) return <div className="pcCargando">Cargando…</div>
	const minimo = d.minGrade
	const baja = esBaja(d.average, minimo)
	const formaDe = (t: MiTareaConNota) => t.delivery
	// Una práctica En Clase no se entrega: aunque el servidor la mande como "en revisión", se muestra en Pendiente como "Se Califica En Clase".
	const enClase = (t: MiTareaConNota) => formaDe(t) === "en_clase"
	const pendientes = [...d.pending, ...d.review.filter(enClase).map((t) => ({ ...t, state: "calificar" as const, overdue: false, repeat: false, closed: false }))]
	const revision = d.review.filter((t) => !enClase(t))

	return (
		<>
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<h2 className="pcT" style={{ margin: 0, fontSize: 26, fontWeight: 800, flex: 1, minWidth: 0 }}>Mis Notas</h2>
				<button type="button" className="pcBtn pcBtnS pcBtnChico" onClick={cargar}>Actualizar</button>
			</div>
			{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}

			{d.scoresVisible ? (
				<section className="pcTarjeta ntMiResumen" aria-label="Mi promedio">
					<Anillo n={d.average} baja={baja} />
					<div className="ntBarrasCol">
						{d.categories.map((c) => <BarraCategoria key={c.name} c={c} minimo={minimo} asistenciaTxt={c.esAsistencia ? textoAsistencia(d.attendance) : null} />)}
						{d.groupAverage !== null ? <div className="pcTenue pcChico" style={{ fontWeight: 600 }}>Promedio Del Grupo: {notaTxt(d.groupAverage)}</div> : null}
					</div>
				</section>
			) : (
				<div className="pcNota">Tu maestro todavía no muestra las notas en el portal. Aquí ves sus comentarios.</div>
			)}
			{d.scoresVisible && d.average !== null && baja ? <div className="pcNota pcNotaOro">Tu promedio está debajo de {notaTxt(minimo)}. Entrega lo pendiente y habla con tu maestro.</div> : null}

			{pendientes.length > 0 ? (
				<>
					<div className="pcSeccion">Pendiente</div>
					{pendientes.map((t) => {
						const clase = enClase(t)
						const oro = !clase && (t.overdue || t.repeat)
						return (
							<button key={t.id} type="button" className={"pcTarjeta ntGr ntGrBoton" + (oro ? " ntPendiente" : "")} onClick={() => onAbrirTarea(t.id)}>
								<div>
									<div className="ntGrTitulo">{t.title}</div>
									<div className="pcChico" style={oro ? { color: "var(--pc-oro-texto)", fontWeight: 700 } : { color: "var(--pc-suave)" }}>
										{clase ? "Se Califica En Clase" + (t.dueLabel ? " · " + t.dueLabel : "") : t.repeat ? "Te Pidieron Repetirla" : t.overdue ? "Sin Entregar · Venció" + (t.dueLabel ? " " + t.dueLabel : "") : t.dueLabel ? "Vence " + t.dueLabel : "Sin Fecha"}
										{" · " + puntosTxt(t.points) + " Pts"}
									</div>
								</div>
								<span className={"pcChip " + (clase ? "" : oro ? "pcChipOro" : "pcChipAcento")} style={{ height: 32, fontSize: 13 }}>{t.closed ? "Cerrada" : clase ? "En Clase" : t.repeat ? "Repetir" : formaDe(t) === "quiz" ? "Hacer Quiz" : "Entregar"}</span>
							</button>
						)
					})}
				</>
			) : null}

			<div className="pcSeccion">Calificadas</div>
			{d.graded.length === 0 ? <div className="pcVacio">Aún No Tienes Tareas Calificadas</div> : (
				<div className="pcTarjeta" style={{ padding: 0, gap: 0, overflow: "hidden" }}>
					{d.graded.map((t) => (
						<div key={t.id} className="ntGr">
							<div>
								<div className="ntGrTitulo">{t.title}</div>
								<div className="pcTenue pcChico">{[t.unit, t.missing ? "No Entregada" : t.returnedLabel].filter(Boolean).join(" · ")}</div>
							</div>
							{t.scoreHidden || t.score === null ? <span className="pcChip pcChipAcento">Calificada</span> : (
								<div className="ntPuntaje">
									<b className={esNotaBaja(t.score, t.points, minimo) || t.missing ? "ntOro" : undefined}>{puntosTxt(t.score)}</b><span> / {puntosTxt(t.points)}</span>
									{t.lateDiscount > 0 ? <div className="pcChico" style={{ color: "var(--pc-oro-texto)", fontWeight: 700 }}>Tarde: −{puntosTxt(t.lateDiscount)} Pts</div> : null}
									{t.kept ? <div className="pcChico pcTenue" style={{ fontWeight: 600 }}>Nota Anterior: No Se Repitió</div> : null}
								</div>
							)}
							{t.comment ? (
								<div className="ntComentario">
									{t.byInitials ? <span className="pcAv pcAvMaestro" style={{ width: 26, height: 26, fontSize: 10 }} title={t.by || undefined} aria-hidden="true">{t.byInitials}</span> : null}
									<span><span style={{ position: "absolute", left: -9999 }}>{t.by ? "Comentario de " + t.by + ": " : "Comentario: "}</span>{t.comment}</span>
								</div>
							) : null}
						</div>
					))}
				</div>
			)}

			{revision.length > 0 ? (
				<>
					<div className="pcSeccion">Entregadas, Sin Nota Aún</div>
					<div className="pcTarjeta" style={{ padding: 0, gap: 0, overflow: "hidden" }}>
						{revision.map((t) => (
							<button key={t.id} type="button" className="ntGr ntGrBoton" onClick={() => onAbrirTarea(t.id)}>
								<div>
									<div className="ntGrTitulo">{t.title}</div>
									<div className="pcTenue pcChico">{t.submittedLabel ? "Entregada " + t.submittedLabel : "Entregada"}{t.late ? " · Tarde" : ""}</div>
								</div>
								<span className="pcChip">En Revisión</span>
							</button>
						))}
					</div>
				</>
			) : null}
			<div className="pcTenue pcChico" style={{ padding: "4px 4px 0" }}>{d.schemeLabel ? "Pesos " + d.schemeLabel + ". " : ""}Una categoría sin notas aún no cuenta: su peso se reparte entre las demás.</div>
		</>
	)
}

export default MisNotasPanel
